import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { updateTicketDraft, deleteTicketDraft, sendTicketDraft, errorMessage } from '../services/api';
import { NtIcon } from './NotionUI';
import { fmtDateTime } from '../utils/format';

/**
 * Bozze di risposta al cliente in attesa (preparate da Claude o da un collega): si rivedono, si correggono e
 * solo "Invia al cliente" manda il messaggio (e l'email di notifica). Si possono anche scartare.
 */
function TicketDrafts({ drafts, onChanged, onSent, onError }) {
  const { t } = useLanguage();
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);

  if (!drafts || drafts.length === 0) return null;

  const textOf = (d) => (edits[d.id] !== undefined ? edits[d.id] : d.body);
  const isDirty = (d) => edits[d.id] !== undefined && edits[d.id] !== d.body;

  const run = async (draft, action) => {
    setBusy(draft.id);
    try {
      await action();
    } catch (err) {
      onError(errorMessage(err, t('tickets.draftError')));
    } finally {
      setBusy(null);
    }
  };

  const save = (d) => run(d, async () => {
    await updateTicketDraft(d.id, textOf(d).trim());
    setEdits(({ [d.id]: _gone, ...rest }) => rest);
    await onChanged();
  });

  const discard = (d) => {
    if (!window.confirm(t('tickets.draftConfirmDiscard'))) return;
    run(d, async () => {
      await deleteTicketDraft(d.id);
      await onChanged();
    });
  };

  const send = (d) => {
    if (!window.confirm(t('tickets.draftConfirmSend'))) return;
    run(d, async () => {
      const res = await sendTicketDraft(d.id, textOf(d).trim());
      setEdits(({ [d.id]: _gone, ...rest }) => rest);
      onSent(res);
    });
  };

  return (
    <div className="td-drafts">
      {drafts.map((d) => (
        <div key={d.id} className={'td-draft' + (d.source === 'claude' ? ' td-draft-claude' : '')}>
          <div className="td-draft-head">
            <span className="td-draft-icon" aria-hidden="true"><NtIcon name={d.source === 'claude' ? 'sparkle' : 'edit'} size={15} /></span>
            <strong>{d.source === 'claude' ? t('tickets.draftFromClaude') : t('tickets.draftFromUser', { name: d.author || '—' })}</strong>
            <span className="td-muted">{fmtDateTime(d.created_at)}</span>
            <span className="td-tag td-tag-note">{t('tickets.draftNotSent')}</span>
          </div>
          <textarea
            rows={Math.min(10, Math.max(3, textOf(d).split('\n').length + 1))}
            value={textOf(d)}
            onChange={(e) => setEdits({ ...edits, [d.id]: e.target.value })}
            aria-label={t('tickets.draftFromClaude')}
          />
          <div className="td-draft-foot">
            <span className="td-muted">{t('tickets.draftHint')}</span>
            <div className="td-draft-actions">
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy === d.id} onClick={() => discard(d)}>{t('tickets.draftDiscard')}</button>
              {isDirty(d) && (
                <button type="button" className="btn btn-secondary btn-sm" disabled={busy === d.id || !textOf(d).trim()} onClick={() => save(d)}>{t('tickets.draftSave')}</button>
              )}
              <button type="button" className="btn btn-sm" disabled={busy === d.id || !textOf(d).trim()} onClick={() => send(d)}>{t('tickets.draftSend')}</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default TicketDrafts;
